import _ from "lodash";
import { v5 as uuidv5 } from "uuid";

import * as UserScript from "./user-script.js";
import { WPD_SPLIT_UUID_NAMESPACE } from "./wpdSplitUuidNamespace.js";

/**
 * Transforms data point coordinates based on rotation.
 * Data points are stored in the original (0-degree) coordinate system.
 * This function transforms them to the specified rotation.
 * @param {Number} x - X coordinate in original rotation
 * @param {Number} y - Y coordinate in original rotation
 * @param {Number} rotation - Target rotation in degrees (0, 90, 180, or 270)
 * @param {Number} originalWidth - Original image width
 * @param {Number} originalHeight - Original image height
 * @return {Object} - Object with transformed x and y coordinates
 */
function transformCoordinatesForRotation(
	x,
	y,
	rotation,
	originalWidth,
	originalHeight,
) {
	// normalize rotation to 0-359 range
	const normalizedRotation = ((rotation % 360) + 360) % 360;

	if (normalizedRotation === 0) {
		return { x, y };
	}

	// determine source rotation image dimensions (always 0 degrees)
	const dimensions = {
		x: originalWidth,
		y: originalHeight,
	};

	let rotatedX, rotatedY;
	switch (normalizedRotation) {
		case 90:
			rotatedX = dimensions.y - y;
			rotatedY = x;
			break;
		case 180:
			rotatedX = dimensions.x - x;
			rotatedY = dimensions.y - y;
			break;
		case 270:
			rotatedX = y;
			rotatedY = dimensions.x - x;
			break;
		default:
			rotatedX = x;
			rotatedY = y;
			break;
	}

	return {
		x: rotatedX,
		y: rotatedY,
	};
}

/**
 * Transforms data point coordinates to display coordinates accounting for rotation.
 * Tries to use wpd.graphicsWidget.getRotatedCoordinates if available, falls back to
 * estimating dimensions from calibration points.
 * @param {Number} x - X coordinate in original rotation
 * @param {Number} y - Y coordinate in original rotation
 * @param {Number} rotation - Target rotation in degrees
 * @param {Object} wpd - The WPD instance (optional)
 * @param {Object} metadata - The metadata object (used for fallback dimension estimation)
 * @return {Object} - Object with displayX and displayY coordinates
 */
function getDisplayCoordinates(x, y, rotation, wpd, metadata) {
	let displayX = x;
	let displayY = y;

	if (rotation === 0) {
		return { displayX, displayY };
	}

	// Try to use graphics widget's method if available
	if (wpd && wpd.graphicsWidget && wpd.graphicsWidget.getRotatedCoordinates) {
		const rotatedCoords = wpd.graphicsWidget.getRotatedCoordinates(
			0, // source rotation (data is stored at 0 degrees)
			rotation, // target rotation
			x,
			y,
		);
		displayX = rotatedCoords.x;
		displayY = rotatedCoords.y;
		console.log(
			`Transformed coordinates from (${x}, ${y}) to (${displayX}, ${displayY}) for rotation ${rotation}°`,
		);
	} else {
		// Fallback: estimate dimensions from metadata
		console.warn(
			"wpd.graphicsWidget not available for coordinate transformation, using fallback method",
		);

		// Try to get image size from the first available axis calibration point
		let imageWidth = 1000; // default fallback
		let imageHeight = 1000; // default fallback

		// Look for calibration points to estimate image size
		if (metadata && metadata.axesColl && metadata.axesColl.length > 0) {
			const firstAxis = metadata.axesColl[0];
			if (
				firstAxis.calibrationPoints &&
				firstAxis.calibrationPoints.length > 0
			) {
				// Use the max px values as rough estimates
				const maxX = Math.max(...firstAxis.calibrationPoints.map((p) => p.px));
				const maxY = Math.max(...firstAxis.calibrationPoints.map((p) => p.py));
				if (maxX > 0) imageWidth = Math.max(imageWidth, maxX);
				if (maxY > 0) imageHeight = Math.max(imageHeight, maxY);
			}
		}

		const transformed = transformCoordinatesForRotation(
			x,
			y,
			rotation,
			imageWidth,
			imageHeight,
		);
		displayX = transformed.x;
		displayY = transformed.y;
		console.log(
			`Transformed coordinates (fallback) from (${x}, ${y}) to (${displayX}, ${displayY}) for rotation ${rotation}° with estimated dimensions ${imageWidth}x${imageHeight}`,
		);
	}

	return { displayX, displayY };
}

/**
 * Calculates the centered scroll position for a data point.
 * @param {Number} x - X coordinate of the data point (in display/screen coordinates)
 * @param {Number} y - Y coordinate of the data point (in display/screen coordinates)
 * @return {Object|null} - Scroll object with scrollLeft and scrollTop, or null if coordinates invalid
 */
function calculateCenteredScroll(x, y) {
	if (x === undefined || y === undefined) {
		return null;
	}

	const wpdIframe = document.getElementById("wpd");
	const wpdDocument =
		wpdIframe?.contentDocument || wpdIframe?.contentWindow?.document;
	const graphicsContainer = wpdDocument?.getElementById("graphicsContainer");

	let viewportWidth = 800;
	let viewportHeight = 600;

	if (graphicsContainer) {
		viewportWidth =
			graphicsContainer.clientWidth || graphicsContainer.offsetWidth;
		viewportHeight =
			graphicsContainer.clientHeight || graphicsContainer.offsetHeight;
	}

	return {
		scrollLeft: Math.max(0, Math.round(x - viewportWidth / 2)),
		scrollTop: Math.max(0, Math.round(y - viewportHeight / 2)),
	};
}

/**
 * Selects the appropriate tree paths for a data point's axis and dataset.
 * @param {Object} wpd - The WPD instance
 * @param {Object} locationInfo - Location info containing dataset, axisName, datasetName
 * @return {Object} - Object containing axis, isGraph, isTable, isText flags
 */
function selectTreePaths(wpd, locationInfo) {
	const plotData = wpd.appData.getPlotData();
	const axis = plotData.getAxesForDataset(locationInfo.dataset);
	const isGraph = wpd.custom.isGraphAxis(axis);
	const isTable = wpd.custom.isTableAxis(axis);
	const isText = wpd.custom.isTextAxis(axis);

	// map axis type to root tree mode
	let rootPath;
	if (isText) {
		rootPath = "/Text";
	} else if (isGraph) {
		rootPath = "/Graph";
	} else if (isTable) {
		rootPath = "/Table";
	} else {
		rootPath = "/Text";
	}

	wpd.tree.selectPath(rootPath);

	// for text axes, there's no axis tree - skip axis tree selection
	if (!isText) {
		const axisTree = wpd.tree.getAxisTree();

		if (axisTree) {
			// select the dataset in the axis tree
			// path format depends on axis type:
			// - Table: /{axisName}
			// - Graph: /{axisName}/{datasetName}
			let axisPath;
			if (isTable) {
				axisPath = `/${locationInfo.axisName}`;
			} else if (isGraph) {
				axisPath = `/${locationInfo.axisName}/${locationInfo.datasetName}`;
			}

			if (axisPath) {
				axisTree.selectPath(axisPath);
			}
		} else {
			console.warn("Axis tree not available after root selection");
		}
	}

	return { axis, isGraph, isTable, isText };
}

/**
 * Selects and highlights a data point in the UI.
 * @param {Object} wpd - The WPD instance
 * @param {Object} locationInfo - Location info containing x, y, dataset
 * @param {Object} axis - The axis object
 * @param {Boolean} isTable - Whether this is a table axis
 */
function selectAndHighlightDataPoint(wpd, locationInfo, axis, isTable) {
	// activate the adjust data point tool and select the data point (not available for tables)
	if (!isTable) {
		wpd.acquireData.adjustPoints();

		const dataset = locationInfo.dataset;
		const cursorDistance = 25;

		const pixelIndex = dataset.selectNearestPixel(
			locationInfo.x,
			locationInfo.y,
			cursorDistance,
		);

		if (pixelIndex >= 0) {
			wpd.graphicsWidget.resetData();
			wpd.graphicsWidget.forceHandlerRepaint();
			wpd.events.dispatch("wpd.dataset.point.select", {
				axes: axis,
				dataset: dataset,
				indexes: [pixelIndex],
			});
		}
	} else {
		// for table axes, simulate a mouse click at the x,y position
		const tool = wpd.graphicsWidget.activeTool;
		if (tool && tool.onMouseClick) {
			tool.onMouseClick(null, null, {
				x: locationInfo.x,
				y: locationInfo.y,
			});
		}
	}
}

/**
 * Gets the pixel offsets for the WPD graphics container context, used to translate graphics container to main window
 * coordinates.
 * @return {Object} - Object containing top, right, bottom, left keys, each containing pixel offsets
 */
function getBounds(wpdDocument) {
	// graphics container pixel boundaries in the context of main viewport
	const dataEntryContainer = document.getElementById("data-entry");
	if (!dataEntryContainer) {
		return {
			top: 0,
			right: 0,
			bottom: 0,
			left: 0,
		};
	}

	const dataEntryRect = dataEntryContainer.getBoundingClientRect();
	const imageRect = wpdDocument
		.getElementById("graphicsContainer")
		.getBoundingClientRect();

	return {
		top: dataEntryRect.top + imageRect.top,
		right: dataEntryRect.left + imageRect.right,
		bottom: dataEntryRect.top + imageRect.bottom,
		left: dataEntryRect.left + imageRect.left,
	};
}

/**
 * Injects a user script into WPD. Contains primary interfacing functions between WPD and Dactyl.
 * @param {VueComponent} dataEntryVM - DataEntry Vue instance
 */
function inject(wpd, wpdDocument, dataEntryVM) {
	const wpdscript = UserScript.generate(wpd, wpdDocument, dataEntryVM);

	window["wpdscript"] = wpdscript;

	wpdscript.run();
}

/**
 * Loads image files into WPD.
 * @param  {Array[Files]} files - Array of files to load into WPD
 * @return {Promise}            - Promise resolving to undefined
 */
async function loadFiles(wpd, files) {
	// load the first supported file
	const index = _getFirstFileIndex(wpd, files);

	wpd.imageManager.initializeFileManager(files, index);
	wpd.appData.setPageManager(null);

	return wpd.imageManager.loadFromFile(files[index]);
}

/**
 * Navigates to a specific data point in already-loaded WPD data.
 * This function switches file/page, sets zoom/scroll, and selects/highlights the data point.
 * Use this when data is already loaded and you just want to navigate to a different location.
 * @param {Object} wpd - The WPD instance
 * @param {String} dataPointUuid - UUID of the data point to navigate to
 * @return {Promise<Object|null>} - Location info if found, null otherwise
 */
async function navigateToDataPoint(wpd, dataPointUuid) {
	if (!dataPointUuid || !wpd) {
		return null;
	}

	// get current WPD state
	const plotData = wpd.appData.getPlotData();
	const fileManager = wpd.appData.getFileManager();
	const serializedMetadata = plotData.serialize(fileManager.getMetadata());
	const metadata = plotData.deserialize(serializedMetadata);

	// find the data point
	const locationInfo = findDataPointByUuid(metadata, dataPointUuid, wpd);
	if (!locationInfo) {
		console.warn(`Data point with UUID ${dataPointUuid} not found`);
		return null;
	}

	// switch to the correct file if needed
	const currentFile = fileManager.currentFileIndex();
	if (locationInfo.file !== currentFile) {
		await fileManager.switch(locationInfo.file);
	}

	// switch to the correct page if needed
	const pageManager = fileManager.pageManagers[locationInfo.file];
	if (pageManager && locationInfo.page !== null) {
		const currentPage = pageManager.currentPage();
		if (locationInfo.page !== currentPage) {
			await pageManager.switch(locationInfo.page);
		}
	}

	// get the rotation for this page and transform coordinates
	const rotation =
		metadata.misc?.rotation?.[locationInfo.file]?.[locationInfo.page] || 0;
	const { displayX, displayY } = getDisplayCoordinates(
		locationInfo.x,
		locationInfo.y,
		rotation,
		wpd,
		metadata,
	);

	// set zoom to 1
	wpd.graphicsWidget.setZoomRatio(1);

	// calculate and set scroll position to center the data point (using rotated coordinates)
	const scroll = calculateCenteredScroll(displayX, displayY);
	if (scroll) {
		wpd.graphicsWidget.setScroll(scroll);
	}

	// select the appropriate tree paths
	if (locationInfo.axisName && locationInfo.datasetName) {
		const { axis, isTable } = selectTreePaths(wpd, locationInfo);

		// select and highlight the data point (use original coordinates for selection)
		selectAndHighlightDataPoint(wpd, locationInfo, axis, isTable);
	}

	return locationInfo;
}

/**
 * Updates metadata object to navigate to a specific data point on next load.
 * Use this when you need to update metadata before calling loadJSON.
 * @param {Object} metadata - The WPD metadata object to update
 * @param {String} dataPointUuid - UUID of the data point to navigate to
 * @param {Object} wpd - The WPD instance
 * @return {Object|null} - Location info if found, null otherwise
 */
function prepareNavigationMetadata(metadata, dataPointUuid, wpd) {
	if (!dataPointUuid || !metadata) {
		return null;
	}

	const locationInfo = findDataPointByUuid(metadata, dataPointUuid, wpd);
	if (!locationInfo) {
		console.warn(`Data point with UUID ${dataPointUuid} not found`);
		return null;
	}

	// override the current file and page in metadata to load the data point's location
	if (!metadata.misc) {
		metadata.misc = {};
	}
	if (!metadata.misc.current) {
		metadata.misc.current = {};
	}

	// set the file index
	metadata.misc.current.file = locationInfo.file;

	// set the page for this specific file
	if (!metadata.misc.current.page) {
		metadata.misc.current.page = {};
	}
	metadata.misc.current.page[locationInfo.file] = locationInfo.page;

	// set zoom to 1
	metadata.misc.current.zoom = 1;

	// get the rotation for this page and transform coordinates
	const rotation =
		metadata.misc?.rotation?.[locationInfo.file]?.[locationInfo.page] || 0;
	const { displayX, displayY } = getDisplayCoordinates(
		locationInfo.x,
		locationInfo.y,
		rotation,
		wpd,
		metadata,
	);

	// calculate scroll position to center the data point (using rotated coordinates)
	const scroll = calculateCenteredScroll(displayX, displayY);
	if (scroll) {
		metadata.misc.current.scroll = scroll;
	}

	return locationInfo;
}

/**
 * Loads WPD JSON into WPD.
 * @param {String} json - JSON string of saved WPD data
 */
async function loadJSON(wpd, dataEntryVM, json, dataPointUuid) {
	const plotData = wpd.appData.getPlotData();
	const metadata = plotData.deserialize(json);

	let locationInfo = null;

	// if a dataPointUuid is provided, find its location and update metadata to load that file/page
	if (dataPointUuid) {
		locationInfo = prepareNavigationMetadata(metadata, dataPointUuid, wpd);
	}

	let data = {};
	if (metadata && Object.keys(metadata).length !== 0) {
		data = metadata;
	}

	const fileManager = wpd.appData.getFileManager();
	await fileManager.loadMetadata(data).then((hasSavedPageAndFile) => {
		if (!hasSavedPageAndFile) {
			// switch to the first supported file if necessary
			const index = _getFirstFileIndex(wpd, fileManager.getFiles());
			if (index !== 0) {
				fileManager._savePageManager();
				fileManager._loadPageManager(index);
				fileManager._saveUndoManager();
				fileManager._loadUndoManager(index);
				wpd.tree.refresh();
			}
		}
	});

	_.forEach(plotData.getDatasets(), (dataset) => {
		// update default color on each loaded data set to include the alpha value
		dataset.colorRGB = new wpd.Color(...wpd.Colors.getValues(wpd.Colors.goodA));

		// check for point groups that need to be upgraded
		if (!dataEntryVM.pointGroupOverhaul && !dataEntryVM.readOnly) {
			if (dataset.hasPointGroups()) {
				const axis = plotData.getAxesForDataset(dataset);

				if (
					wpd.pointGroups.isSupportedAxis(axis) &&
					!axis.getMetadata()?.pointGroups
				) {
					try {
						// upgrade point groups to the new format
						wpd.pointGroups.updateToNewFormat(axis, dataset);
					} catch (error) {
						console.error(error);
					}
				}
			}
		}
	});

	// render studies tree
	wpd.tagGroups.refreshStudies();

	// select the axis and dataset if we found the data point
	// we need to select the root tree item first, then the axis tree will be created
	if (locationInfo && locationInfo.axisName && locationInfo.datasetName) {
		const { axis, isTable } = selectTreePaths(wpd, locationInfo);
		selectAndHighlightDataPoint(wpd, locationInfo, axis, isTable);
	}

	// clear the loading overlay
	dataEntryVM.loading = false;
}

/**
 * Serializes current WPD data to a JSON string.
 * @return {String} - JSON string of WPD data
 */
function saveJSON(wpd) {
	const plotData = wpd.appData.getPlotData();
	const metadata = wpd.appData.getFileManager().getMetadata();
	return JSON.stringify(plotData.serialize(metadata));
}

/**
 * Sets metadata on a given dataset.
 * Used for storing cached metadata when the dialog is open while autosaving is triggered.
 * @param {Object} payload - Payload object containing the container, indexes, and metadata
 */
function setMetadata(wpd, payload) {
	if (payload.container && payload.indexes) {
		if (payload.indexes.length === 1) {
			// set rowIndex for table axis data points
			if (payload.rowIndex != null) {
				payload.metadata.rowIndex = payload.rowIndex;
			}

			// single point selected
			wpd.custom.saveDataPoint(
				payload.container,
				payload.indexes[0],
				payload.metadata,
			);
		} else {
			// multiple points selected
			wpd.custom.saveDataPoints(
				payload.container,
				payload.indexes,
				payload.metadata,
			);
		}

		// sync metadata keys on data set
		wpd.custom.syncMetadataKeys(payload.container);

		// repaint UI
		wpd.custom.repaintDataPoints(payload.container);
	}
}

/**
 * Sets options object values.
 * @param {Object} options    - Options object defined in interfaces.js
 * @param {Object} newOptions - New values to add to or override in options object
 */
function setOptions(options, newOptions) {
	_.assign(options, newOptions);
}

/**
 * Gets current location information: file and page indexes
 */
function getCurrentLocationInfo(wpd) {
	return {
		file: wpd.appData.getFileManager().currentFileIndex(),
		page: wpd.appData.getPageManager().currentPage(),
	};
}

/**
 * Returns index the of first file that is of a supported type, otherwise 0.
 * @param {Object} wpd   - WebPlotDigitizer instance
 * @param {Array}  files - Files loaded into WPD
 */
function _getFirstFileIndex(wpd, files) {
	const index = files.findIndex(wpd.utils.isSupportedFile);

	return index < 0 ? 0 : index;
}

function disableUndo(wpdDocument) {
	const undoButton = wpdDocument.getElementById("wpd-undo-button");
	if (undoButton) {
		undoButton.toggleAttribute("disabled", true);
	}
}

function disableRedo(wpdDocument) {
	const redoButton = wpdDocument.getElementById("wpd-redo-button");
	if (redoButton) {
		redoButton.toggleAttribute("disabled", true);
	}
}

function enableUndo(wpdDocument) {
	const undoButton = wpdDocument.getElementById("wpd-undo-button");
	if (undoButton) {
		undoButton.toggleAttribute("disabled", false);
	}
}

function enableRedo(wpdDocument) {
	const redoButton = wpdDocument.getElementById("wpd-redo-button");
	if (redoButton) {
		redoButton.toggleAttribute("disabled", false);
	}
}

function selectPath(wpd, rootPath, axisPath) {
	if (rootPath) {
		wpd.tree.selectPath(rootPath);
		if (axisPath) {
			wpd.tree.selectAxisPath(axisPath);
		}
	}
}

function getSelectedPaths(wpd) {
	return {
		root: wpd.tree.getRootTreeSelectedPath(),
		axis: wpd.tree.getAxisTreeSelectedPath(),
	};
}

/**
 * Finds a data point by UUID in the metadata and returns its location information.
 * @param {Object} metadata - The deserialized WPD metadata object
 * @param {String} uuid - The UUID of the data point to find
 * @returns {Object|null} - Object containing {file, page, x, y, dataset, dataIndex} or null if not found
 */
/**
 * Finds a data point by UUID in the metadata and returns its location information.
 * @param {Object} metadata - The deserialized WPD metadata object
 * @param {String} uuid - The UUID of the data point to find
 * @param {Object} wpd - Optional WPD instance to look up axis information
 * @returns {Object|null} - Object containing {file, page, x, y, dataset, dataIndex, axisName} or null if not found
 */
function findDataPointByUuid(metadata, uuid, wpd) {
	if (!metadata || !uuid) {
		return null;
	}

	// The point's own uuid first, exactly as before DCTL-2701; only on a miss
	// try the per-tag-group uuids the Dactylizer derives for a cell split
	// across tag groups (QC comment and rework links carry those).
	return (
		findDataPointWhere(metadata, wpd, (pointMetadata) => {
			return pointMetadata.uuid === uuid;
		}) ??
		findDataPointWhere(metadata, wpd, (pointMetadata) => {
			return isSplitTagGroupUuid(pointMetadata, uuid);
		})
	);
}

/**
 * DCTL-2701. Whether `uuid` is one of the per-tag-group uuids the Dactylizer
 * derives for this point (api `splitTagGroupUuid`). Only a point with two or
 * more tag group entries can have been split, so one-tab points cost nothing.
 * @param {Object} pointMetadata - A data point's metadata
 * @param {String} uuid - The uuid being looked up
 * @returns {Boolean}
 */
function isSplitTagGroupUuid(pointMetadata, uuid) {
	const tagGroups = pointMetadata.tagGroups;
	if (!pointMetadata.uuid || !Array.isArray(tagGroups)) return false;
	if (tagGroups.length < 2) return false;
	return tagGroups.some(
		(tagGroup) =>
			!!tagGroup?.uuid &&
			uuidv5(
				`${pointMetadata.uuid}:${tagGroup.uuid}`,
				WPD_SPLIT_UUID_NAMESPACE,
			) === uuid,
	);
}

/**
 * The location of the first data point whose metadata satisfies `matches`.
 * @param {Object} metadata - The deserialized WPD metadata object
 * @param {Object} wpd - Optional WPD instance to look up axis information
 * @param {Function} matches - Predicate over a data point's metadata
 * @returns {Object|null} - Object containing {file, page, x, y, dataset, dataIndex, axisName} or null if not found
 */
function findDataPointWhere(metadata, wpd, matches) {
	// Build a page lookup: dataset name → page number
	// from page.datasets which organizes data by page
	const datasetPageMap = {};
	if (metadata.page?.datasets) {
		for (const [pageNum, datasets] of Object.entries(metadata.page.datasets)) {
			if (!Array.isArray(datasets)) continue;
			for (const dataset of datasets) {
				if (dataset.name) {
					datasetPageMap[dataset.name] = parseInt(pageNum, 10);
				}
			}
		}
	}

	// Search through file.datasets (has correct file index)
	if (metadata.file?.datasets) {
		for (const [fileNum, datasets] of Object.entries(metadata.file.datasets)) {
			if (!Array.isArray(datasets)) continue;

			for (const dataset of datasets) {
				if (!dataset._dataPoints || !Array.isArray(dataset._dataPoints)) {
					continue;
				}

				for (let i = 0; i < dataset._dataPoints.length; i++) {
					const dataPoint = dataset._dataPoints[i];

					if (dataPoint.metadata && matches(dataPoint.metadata)) {
						// Look up page from the page.datasets map, fall back to misc.current.page
						const page =
							datasetPageMap[dataset.name] ??
							metadata.misc?.current?.page?.[fileNum] ??
							null;

						const result = {
							file: parseInt(fileNum, 10),
							page,
							x: dataPoint.x,
							y: dataPoint.y,
							dataset: dataset,
							dataIndex: i,
							datasetName: dataset.name,
						};

						if (wpd) {
							const plotData = wpd.appData.getPlotData();
							const axis = plotData.getAxesForDataset(dataset);
							if (axis) {
								result.axisName = axis.name;
							}
						}

						return result;
					}
				}
			}
		}
	}

	// Fallback: search page.datasets directly (for data not in file.datasets)
	if (metadata.page?.datasets) {
		const currentFile = metadata.misc?.current?.file ?? 0;
		for (const [pageNum, datasets] of Object.entries(metadata.page.datasets)) {
			if (!Array.isArray(datasets)) continue;

			for (const dataset of datasets) {
				if (!dataset._dataPoints || !Array.isArray(dataset._dataPoints)) {
					continue;
				}

				for (let i = 0; i < dataset._dataPoints.length; i++) {
					const dataPoint = dataset._dataPoints[i];

					if (dataPoint.metadata && matches(dataPoint.metadata)) {
						const result = {
							file: currentFile,
							page: parseInt(pageNum, 10),
							x: dataPoint.x,
							y: dataPoint.y,
							dataset: dataset,
							dataIndex: i,
							datasetName: dataset.name,
						};

						if (wpd) {
							const plotData = wpd.appData.getPlotData();
							const axis = plotData.getAxesForDataset(dataset);
							if (axis) {
								result.axisName = axis.name;
							}
						}

						return result;
					}
				}
			}
		}
	}

	return null;
}

export {
	disableUndo,
	disableRedo,
	enableUndo,
	enableRedo,
	findDataPointByUuid,
	getBounds,
	getCurrentLocationInfo,
	getSelectedPaths,
	inject,
	loadFiles,
	loadJSON,
	navigateToDataPoint,
	prepareNavigationMetadata,
	saveJSON,
	selectPath,
	setMetadata,
	setOptions,
};
